import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { recommendationService } from "../../api/recommendations";
import Swal from "sweetalert2";
import {
  BarChart3,
  Eye,
  MousePointerClick,
  Share2,
  AlertCircle,
  CheckCircle,
  Plus,
} from "lucide-react";

const RecruiterRecommendationsPage = () => {
  const navigate = useNavigate();
  const [recommendations, setRecommendations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [selectedRec, setSelectedRec] = useState(null);
  const [stats, setStats] = useState(null);

  // Fetch recommendations sent by recruiter
  useEffect(() => {
    const fetchRecommendations = async () => {
      try {
        setLoading(true);
        const data = await recommendationService.getSent();
        setRecommendations(data.results || data);
        setError("");
      } catch (err) {
        setError("Failed to load recommendations");
        console.error(err);
      } finally {
        setLoading(false);
      }
    };

    fetchRecommendations();
  }, []);

  // Fetch stats for selected recommendation
  useEffect(() => {
    if (selectedRec) {
      const fetchStats = async () => {
        try {
          const data = await recommendationService.getStats(selectedRec.id);
          setStats(data);
        } catch (err) {
          console.error("Error fetching stats:", err);
        }
      };
      fetchStats();
    }
  }, [selectedRec]);

  const handleShare = async (recId, channel, recipientId = null) => {
    // Show confirmation before generating share link
    const channelNames = {
      whatsapp: "WhatsApp",
      linkedin: "LinkedIn",
      twitter: "X (Twitter)",
      copy: "Clipboard",
    };
    
    const confirmResult = await Swal.fire({
      title: "Confirm Share",
      text: `Generate a trackable link and share via ${channelNames[channel]}?`,
      icon: "question",
      showCancelButton: true,
      confirmButtonColor: "#2563eb",
      cancelButtonColor: "#6b7280",
      confirmButtonText: "Yes, share it!"
    });

    if (!confirmResult.isConfirmed) return;

    try {
      const shareData = await recommendationService.createShare(
        recId,
        channel,
        recipientId
      );
      
      if (channel === "copy") {
        navigator.clipboard.writeText(shareData.share_url);
        Swal.fire({
          title: "Copied!",
          text: "Share link copied to clipboard.",
          icon: "success",
          timer: 2000,
          showConfirmButton: false
        });
      } else if (shareData.platform_urls && shareData.platform_urls[channel]) {
        window.open(shareData.platform_urls[channel], "_blank");
        
        // Let's also refresh stats so the new share count appears
        const newStats = await recommendationService.getStats(recId);
        setStats(newStats);
        
        Swal.fire({
          title: "Shared!",
          text: `Link generated and opened in ${channelNames[channel]}.`,
          icon: "success",
          timer: 2000,
          showConfirmButton: false
        });
      }
    } catch (err) {
      Swal.fire("Error", "Failed to create share link", "error");
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <p className="text-gray-600">Loading recommendations...</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 py-8 px-4">
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <div className="flex justify-between items-center mb-8">
          <div>
            <h1 className="text-3xl font-bold text-gray-900">
              Job Recommendations
            </h1>
            <p className="text-gray-600 mt-2">
              Track and manage your job recommendations
            </p>
          </div>
          <button
            onClick={() => navigate("/recruiter/send-recommendation")}
            className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition"
          >
            <Plus size={20} />
            Send Recommendation
          </button>
        </div>

        {/* Error Message */}
        {error && (
          <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg flex items-start gap-3">
            <AlertCircle className="text-red-600 mt-0.5 flex-shrink-0" />
            <p className="text-red-800">{error}</p>
          </div>
        )}

        {/* Empty State */}
        {recommendations.length === 0 && (
          <div className="bg-white rounded-lg shadow-md p-12 text-center">
            <p className="text-gray-600 mb-4">
              You haven't sent any recommendations yet
            </p>
            <button
              onClick={() => navigate("/recruiter/send-recommendation")}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition"
            >
              Send Your First Recommendation
            </button>
          </div>
        )}

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Recommendations List */}
          <div className="lg:col-span-2">
            <div className="space-y-4">
              {recommendations.map((rec) => (
                <div
                  key={rec.id}
                  onClick={() => setSelectedRec(rec)}
                  className={`p-4 border-2 rounded-lg cursor-pointer transition ${
                    selectedRec?.id === rec.id
                      ? "border-blue-600 bg-blue-50"
                      : "border-gray-200 bg-white hover:border-gray-300"
                  }`}
                >
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <h3 className="font-semibold text-lg">{rec.job.title}</h3>
                      <p className="text-sm text-gray-600">
                        📍 {rec.job.location}
                      </p>
                      <p className="text-sm text-gray-700 mt-2 line-clamp-2">
                        {rec.message}
                      </p>
                    </div>
                    <div className="text-right">
                      <div className="text-sm text-gray-500">
                        Sent: {new Date(rec.created_at).toLocaleDateString()}
                      </div>
                      <div className="flex gap-2 mt-2 text-xs font-medium">
                        <span className="px-2 py-1 bg-blue-100 text-blue-700 rounded">
                          {rec.recipient_count} recipients
                        </span>
                        <span className="px-2 py-1 bg-green-100 text-green-700 rounded">
                          {rec.viewed_count} viewed
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Stats Panel */}
          {selectedRec && stats && (
            <div className="bg-white rounded-lg shadow-md p-6">
              <h2 className="text-xl font-semibold mb-4">Statistics</h2>

              {/* Overview Stats */}
              <div className="space-y-3 mb-6">
                <div className="flex items-center justify-between p-3 bg-gray-50 rounded">
                  <span className="text-sm text-gray-600">Total Recipients</span>
                  <span className="text-2xl font-bold text-gray-900">
                    {stats.total_recipients}
                  </span>
                </div>
                <div className="flex items-center justify-between p-3 bg-blue-50 rounded">
                  <span className="flex items-center gap-2 text-sm text-gray-600">
                    <Eye size={16} />
                    Viewed
                  </span>
                  <span className="text-2xl font-bold text-blue-600">
                    {stats.viewed_count}
                  </span>
                </div>
                <div className="flex items-center justify-between p-3 bg-green-50 rounded">
                  <span className="flex items-center gap-2 text-sm text-gray-600">
                    <MousePointerClick size={16} />
                    Clicked
                  </span>
                  <span className="text-2xl font-bold text-green-600">
                    {stats.clicked_count}
                  </span>
                </div>
                <div className="flex items-center justify-between p-3 bg-purple-50 rounded">
                  <span className="flex items-center gap-2 text-sm text-gray-600">
                    <Share2 size={16} />
                    Shared
                  </span>
                  <span className="text-2xl font-bold text-purple-600">
                    {stats.shared_count}
                  </span>
                </div>
              </div>

              <hr className="my-4" />

              {/* Recipient Details */}
              <h3 className="font-semibold mb-3">Recipients</h3>
              <div className="space-y-3 max-h-96 overflow-y-auto">
                {stats.recipients.map((recipient) => (
                  <div key={recipient.id} className="p-3 bg-gray-50 rounded">
                    <p className="font-medium text-sm">{recipient.professional.name}</p>
                    <p className="text-xs text-gray-600">
                      {recipient.professional.email}
                    </p>
                    <div className="flex gap-2 mt-2 flex-wrap">
                      {recipient.viewed && (
                        <span className="text-xs bg-blue-100 text-blue-700 px-2 py-1 rounded">
                          ✓ Viewed
                        </span>
                      )}
                      {recipient.clicked && (
                        <span className="text-xs bg-green-100 text-green-700 px-2 py-1 rounded">
                          ✓ Clicked
                        </span>
                      )}
                      {recipient.shared && (
                        <span className="text-xs bg-purple-100 text-purple-700 px-2 py-1 rounded">
                          ✓ Shared
                        </span>
                      )}
                      {recipient.email_sent && (
                        <span className="text-xs bg-orange-100 text-orange-700 px-2 py-1 rounded">
                          ✓ Email sent
                        </span>
                      )}
                    </div>
                  </div>
                ))}
              </div>

              {/* Share Options */}
              <div className="mt-6 pt-4 border-t">
                <h3 className="font-semibold mb-3 text-sm">Share Externally</h3>
                <div className="space-y-2">
                  <button
                    onClick={() => handleShare(selectedRec.id, "whatsapp")}
                    className="w-full px-3 py-2 bg-green-500 text-white text-sm rounded hover:bg-green-600 transition flex items-center justify-center gap-2"
                  >
                    <Share2 size={16} />
                    WhatsApp
                  </button>
                  <button
                    onClick={() => handleShare(selectedRec.id, "linkedin")}
                    className="w-full px-3 py-2 bg-blue-700 text-white text-sm rounded hover:bg-blue-800 transition flex items-center justify-center gap-2"
                  >
                    <Share2 size={16} />
                    LinkedIn
                  </button>
                  <button
                    onClick={() => handleShare(selectedRec.id, "twitter")}
                    className="w-full px-3 py-2 bg-black text-white text-sm rounded hover:bg-gray-800 transition flex items-center justify-center gap-2"
                  >
                    <Share2 size={16} />X
                  </button>
                  <button
                    onClick={() => handleShare(selectedRec.id, "copy")}
                    className="w-full px-3 py-2 bg-gray-500 text-white text-sm rounded hover:bg-gray-600 transition flex items-center justify-center gap-2"
                  >
                    <Share2 size={16} />
                    Copy Link
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default RecruiterRecommendationsPage;
